import { sectionRenderer } from '@theme/section-renderer';
import { startViewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

const CATEGORY_PARAMETER = 'categoria';
const SUBCATEGORY_PARAMETER = 'subcategoria';
const CATALOG_PARAMETERS = new Set([CATEGORY_PARAMETER, SUBCATEGORY_PARAMETER, 'section_id']);

/**
 * Keeps the catalog on /collections/all while rendering products from the
 * selected category collection.
 */
class LinovaCatalogNavigation extends HTMLElement {
  #initialSyncFrame = 0;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('popstate', this.#handlePopState);

    if (document.readyState === 'complete') {
      this.#initialSyncFrame = requestAnimationFrame(this.#handleInitialLoad);
    } else {
      window.addEventListener('load', this.#handleInitialLoad, { once: true });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('load', this.#handleInitialLoad);
    cancelAnimationFrame(this.#initialSyncFrame);
  }

  get sectionId() {
    const sectionId = this.getAttribute('section-id');
    if (!sectionId) throw new Error('The section-id attribute is required');
    return sectionId;
  }

  #handleClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[data-catalog-source]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    const sourcePath = link.dataset.catalogSource;
    if (!sourcePath) return;

    event.preventDefault();

    const displayUrl = new URL(link.href);
    const currentUrl = new URL(window.location.href);
    const sortBy = currentUrl.searchParams.get('sort_by');

    if (sortBy) displayUrl.searchParams.set('sort_by', sortBy);
    displayUrl.searchParams.delete('page');

    this.#navigate(displayUrl, new URL(sourcePath, window.location.origin), true);
  };

  #handleInitialLoad = () => {
    this.#syncFromLocation(false);
  };

  #handlePopState = () => {
    this.#syncFromLocation(true);
  };

  /**
   * @param {boolean} animate
   */
  #syncFromLocation = (animate) => {
    const displayUrl = new URL(window.location.href);
    const hasCatalogState =
      displayUrl.searchParams.has(CATEGORY_PARAMETER) || displayUrl.searchParams.has(SUBCATEGORY_PARAMETER);

    if (!displayUrl.pathname.endsWith('/collections/all') && !hasCatalogState) return;

    const sourceUrl = this.#getSourceUrlFromState(displayUrl);
    const currentSource = new URL(this.getAttribute('source-url') || '/collections/all', window.location.origin);

    if (sourceUrl.pathname === currentSource.pathname) return;

    this.#navigate(displayUrl, sourceUrl, false, animate);
  };

  /**
   * @param {URL} displayUrl
   * @returns {URL}
   */
  #getSourceUrlFromState(displayUrl) {
    const handle =
      displayUrl.searchParams.get(SUBCATEGORY_PARAMETER) ||
      displayUrl.searchParams.get(CATEGORY_PARAMETER) ||
      'all';
    const safeHandle = /^[a-z0-9-]+$/.test(handle) ? handle : 'all';

    return new URL(`/collections/${safeHandle}`, window.location.origin);
  }

  /**
   * @param {URL} sourceUrl
   * @param {URL} displayUrl
   * @returns {URL}
   */
  #getRenderUrl(sourceUrl, displayUrl) {
    const renderUrl = new URL(sourceUrl);
    renderUrl.search = '';

    for (const [parameter, value] of displayUrl.searchParams) {
      if (!CATALOG_PARAMETERS.has(parameter)) {
        renderUrl.searchParams.append(parameter, value);
      }
    }

    return renderUrl;
  }

  /**
   * @param {URL} displayUrl
   * @param {URL} sourceUrl
   * @param {boolean} updateHistory
   * @param {boolean} [animate]
   */
  async #navigate(displayUrl, sourceUrl, updateHistory, animate = true) {
    const currentDisplayUrl = new URL(window.location.href);
    const currentSource = new URL(this.getAttribute('source-url') || '/collections/all', window.location.origin);

    if (displayUrl.toString() === currentDisplayUrl.toString() && sourceUrl.pathname === currentSource.pathname) {
      return;
    }

    if (updateHistory) {
      history.pushState({ catalogSource: sourceUrl.pathname }, '', displayUrl);
    }

    const resultsList = this.closest('results-list');
    resultsList?.setAttribute('aria-busy', 'true');
    document.dispatchEvent(new Event(ThemeEvents.FilterUpdate));

    try {
      const renderUrl = this.#getRenderUrl(sourceUrl, displayUrl);

      if (animate) {
        let renderPromise = Promise.resolve('');

        await startViewTransition(
          () => {
            renderPromise = sectionRenderer.renderSection(this.sectionId, { url: renderUrl });
            return renderPromise;
          },
          ['product-grid']
        );
        await renderPromise;
      } else {
        await sectionRenderer.renderSection(this.sectionId, { url: renderUrl });
      }

      document.querySelector('results-list')?.removeAttribute('aria-busy');
      this.#scrollToCatalogStart();
      this.#centerActiveCategory();
    } catch (error) {
      console.error('Unable to update the catalog category', error);
      resultsList?.removeAttribute('aria-busy');

      if (updateHistory) {
        window.location.assign(sourceUrl);
      }
    }
  }

  #scrollToCatalogStart() {
    const catalog = document.querySelector('.linova-collection-wrapper');
    if (!catalog) return;

    const top = catalog.getBoundingClientRect().top + window.scrollY - 16;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'instant' });
  }

  #centerActiveCategory() {
    requestAnimationFrame(() => {
      const activeCategory = document.querySelector('.linova-collection-sidebar__link.is-active');
      const categoryNavigation = activeCategory?.closest('.linova-collection-sidebar__nav');

      if (categoryNavigation && categoryNavigation.scrollWidth > categoryNavigation.clientWidth) {
        categoryNavigation.scrollLeft =
          activeCategory.offsetLeft - (categoryNavigation.clientWidth - activeCategory.offsetWidth) / 2;
      }
    });
  }
}

if (!customElements.get('linova-catalog-navigation')) {
  customElements.define('linova-catalog-navigation', LinovaCatalogNavigation);
}
